<template>
  <div class="pc__swatch-container">
    <div class="pc__swatches">
      <div
        v-if="!disableScrollIcon"
        :class="['pc__swatches-prev', { 'pc__swatches--blur': leftScroll === 0 }]"
        @click="scrollSwatches('left')"
        role="button"
        :aria-label="leftScroll === 0 ? 'No more items to scroll left' : 'Scroll left through color options'"
        :aria-disabled="leftScroll === 0 ? true : false"
        @keydown="(e) => e.key === 'Enter' && scrollSwatches('left')"
      >
        <svg class="pc__swatches-prev-icon">
          <use xlink:href="#icon-prev"></use>
        </svg>
      </div>
      <div ref="swatchWrapper" class="pc__swatches-wrapper">
        <div
          v-for="(value, name) in colorOptions"
          tabindex="0"
          :key="name"
          :class="['pc__swatchButton', { active: name === selectedColor }]"
          @click="(e) => switchActiveSwatch(e, name)"
          @keydown="(e) => handleKeyDownOnSwatches(e, name)"
          role="button"
          :aria-label="'Select color ' + name"
          :aria-pressed="name === selectedColor"
        >
          <ImageField
            v-if="value.image"
            class="pc__swatchImage"
            :obj="{ url: value.image.src, alt: value.image.alt, width: 32, height: 32 }"
            :isLoadingLazy="isLoadingLazy"
          />
        </div>
      </div>
      <div
        v-if="!disableScrollIcon"
        :class="['pc__swatches-next', { 'pc__swatches--blur': leftScroll === rightScroll }]"
        @click="scrollSwatches('right')"
        role="button"
        :aria-label="leftScroll === rightScroll ? 'No more items to scroll right' : 'Scroll right through color options'"
        :aria-disabled="leftScroll === rightScroll ? true : false"
        @keydown="(e) => e.key === 'Enter' && scrollSwatches('right')"
      >
        <svg class="pc__swatches-next-icon">
          <use xlink:href="#icon-next"></use>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, toRefs, onBeforeMount } from 'vue';
  import ImageField from '../image.vue';

  const props = defineProps({
    colorOptions: { type: Object, required: true },
    selectedColor: { type: String, required: true },
    isLoadingLazy: { type: Boolean, default: false },
    windowWidth: { type: Number, required: true },
  });

  const { colorOptions, selectedColor, isLoadingLazy, windowWidth } = toRefs(props);

  const emit = defineEmits(['color-change']);

  const swatchWrapper = ref(null);
  const leftScroll = ref(0);
  const rightScroll = ref(0);

  onBeforeMount(() => {
    setTimeout(() => {
      updateScrollStates();
    }, 200);
  });

  const disableScrollIcon = computed(() => {
    if (swatchWrapper.value) {
      const isScroll = swatchWrapper.value.offsetWidth === swatchWrapper.value.scrollWidth;
      return windowWidth.value > 1023 ? isScroll : isScroll;
    }
  });

  watch(colorOptions, () => {
    swatchWrapper.value = null;
    setTimeout(() => {
      updateScrollStates();
    }, 200);
  });

  const switchActiveSwatch = (e, name) => {
    e.preventDefault();
    emit('color-change', name);
  };

  const scrollSwatches = (pos) => {
    const element = swatchWrapper.value;
    if (pos === 'left') {
      element.scrollLeft -= 100;
    } else {
      element.scrollLeft += 100;
    }
    updateScrollStates();
  };

  const handleKeyDownOnSwatches = (event, name) => {
    if (event.key === 'Enter' || event.key === ' ') {
      switchActiveSwatch(event, name);
    }
  };

  const updateScrollStates = () => {
    if (swatchWrapper.value) {
      leftScroll.value = swatchWrapper.value.scrollLeft;
      rightScroll.value = swatchWrapper.value.scrollWidth - swatchWrapper.value.offsetWidth;
    }
  };
</script>
